/**
 * AuthContext.tsx
 *
 * Single source of truth for authentication state.
 *
 * The JWT lives in an httpOnly cookie — the frontend never sees it.
 * On mount we call GET /api/auth/me to restore an existing session.
 *
 * Microsoft login:
 *   The browser navigates to GET /api/auth/microsoft (backend).
 *   The backend performs the full OAuth2 confidential-client flow with
 *   Microsoft, calls Graph API with the client_secret, issues the JWT
 *   cookie, then redirects the browser back to the frontend.
 *   No MSAL or client-side tokens involved.
 */

import {
  createContext, useContext, useState, useEffect, useCallback,
  type ReactNode,
} from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

export type UserRole = 'employee' | 'agent' | 'interviewer' | 'hr' | 'admin'

export interface AuthUser {
  id:          string
  email:       string
  displayName: string | null
  role:        UserRole
}

interface AuthContextValue {
  /** Authenticated user, or null if not signed in */
  user:             AuthUser | null
  /** True while the initial /api/auth/me check is in flight */
  initialising:     boolean
  /** True if user is set */
  isAuthenticated:  boolean
  /** Sign in with email + password */
  loginLocal:       (email: string, password: string) => Promise<void>
  /**
   * Navigates the browser to GET /api/auth/microsoft.
   * The backend handles the full OAuth2 flow and redirects back.
   * @param returnTo  path to redirect to after successful login (default: '/')
   */
  loginMicrosoft:   (returnTo?: string) => void
  /** True when the backend has Microsoft sign-in configured (GET /api/auth/providers) */
  microsoftEnabled: boolean
  /** Sign out — clears server session + JWT cookie */
  logout:           () => Promise<void>
  /** Last auth error, if any */
  authError:        string | null
  clearAuthError:   () => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,         setUser]         = useState<AuthUser | null>(null)
  const [initialising, setInitialising] = useState(true)
  const [authError,    setAuthError]    = useState<string | null>(null)
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false)

  // ── On mount: restore session from httpOnly cookie ────────────────────────
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => (res.ok ? res.json() as Promise<AuthUser> : Promise.resolve(null)))
      .then(u => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setInitialising(false))
  }, [])

  // ── Which sign-in methods the backend offers ──────────────────────────────
  // Hidden by default; only shown once the backend confirms it's configured.
  useEffect(() => {
    fetch('/api/auth/providers', { credentials: 'include' })
      .then(res => (res.ok ? res.json() as Promise<{ microsoft?: boolean }> : Promise.resolve(null)))
      .then(p => setMicrosoftEnabled(p?.microsoft === true))
      .catch(() => setMicrosoftEnabled(false))
  }, [])

  // ── Local login ───────────────────────────────────────────────────────────
  const loginLocal = useCallback(async (email: string, password: string) => {
    setAuthError(null)
    const res = await fetch('/api/auth/login', {
      method:      'POST',
      headers:     { 'Content-Type': 'application/json' },
      credentials: 'include',
      body:        JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { message?: string }
      throw new Error(body.message ?? 'Invalid email or password')
    }
    const u = await res.json() as AuthUser
    setUser(u)
  }, [])

  // ── Microsoft login ───────────────────────────────────────────────────────
  // Navigate to the backend OAuth2 entry point. The backend holds the
  // client_secret, performs the full auth code exchange, calls Graph API,
  // issues the JWT cookie, and redirects back to the frontend.
  const loginMicrosoft = useCallback((returnTo = '/') => {
    const params = returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : ''
    window.location.href = `/api/auth/microsoft${params}`
  }, [])

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    setAuthError(null)
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch { /* best-effort */ }
    setUser(null)
  }, [])

  const clearAuthError = useCallback(() => setAuthError(null), [])

  return (
    <AuthContext.Provider value={{
      user,
      initialising,
      isAuthenticated: user !== null,
      loginLocal,
      loginMicrosoft,
      microsoftEnabled,
      logout,
      authError,
      clearAuthError,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
